import { useEffect, useRef, useState } from "react";
import { ArrowDownIcon } from "@heroicons/react/24/outline";
import { QuinnMessage, QuinnRole } from "../../types";
import type { QuinnRatingEnum } from "../../../../generated/enums.ts";
import { QuinnMessageView } from "../Message";
import { QuinnEmptyState } from "../EmptyState";

type QuinnThreadProps = {
    messages: QuinnMessage[];
    isBusy: boolean;
    isHydrating: boolean;
    pageLabel: string;
    suggestions: string[];
    onPickSuggestion: (suggestion: string) => void;
    onRetry: (message: QuinnMessage) => void;
    onAnswerPrompt: (messageId: string, values: string[], label: string) => void;
    onRate: (runId: string, rating: QuinnRatingEnum) => void;
    contentClassName?: string;
};

export function QuinnThread({
    messages,
    isBusy,
    isHydrating,
    pageLabel,
    suggestions,
    onPickSuggestion,
    onRetry,
    onAnswerPrompt,
    onRate,
    contentClassName = "",
}: QuinnThreadProps) {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [isPinnedToBottom, setIsPinnedToBottom] = useState(true);
    const isPinnedRef = useRef(true);

    isPinnedRef.current = isPinnedToBottom;

    useEffect(() => {
        const container = scrollRef.current;
        if (!container || !isPinnedRef.current) return;
        container.scrollTop = container.scrollHeight;
    }, [messages]);

    function syncPinnedState() {
        const container = scrollRef.current;
        if (!container) return;
        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        setIsPinnedToBottom(distanceFromBottom < 64);
    }

    function scrollToBottom() {
        const container = scrollRef.current;
        if (!container) return;
        container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
        setIsPinnedToBottom(true);
    }

    const lastQuinnMessageId = [...messages].reverse().find((message) => message.role === QuinnRole.Quinn)?.id;

    return (
        <div className="relative min-h-0 flex-1">
            <div
                ref={scrollRef}
                onWheel={syncPinnedState}
                onTouchMove={syncPinnedState}
                onKeyDown={syncPinnedState}
                className="h-full overflow-y-auto px-4 py-4"
            >
                <div className={`h-full ${contentClassName}`}>
                    {messages.length === 0 && !isHydrating && (
                        <QuinnEmptyState pageLabel={pageLabel} suggestions={suggestions} onPick={onPickSuggestion} />
                    )}

                    {messages.length === 0 && isHydrating && (
                        <div className="space-y-2 pt-2">
                            {[0, 1, 2].map((index) => (
                                <div key={index} className="h-12 animate-pulse rounded-xl bg-gray-100" />
                            ))}
                        </div>
                    )}

                    <div className="space-y-4">
                        {messages.map((message) => (
                            <QuinnMessageView
                                key={message.id}
                                message={message}
                                isBusy={isBusy}
                                canRetry={message.id === lastQuinnMessageId && !isBusy && Boolean(message.error)}
                                onRetry={() => onRetry(message)}
                                onAnswerPrompt={(values, label) => onAnswerPrompt(message.id, values, label)}
                                onRate={onRate}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {!isPinnedToBottom && messages.length > 0 && (
                <button
                    type="button"
                    onClick={scrollToBottom}
                    className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full border border-gray-200 bg-white p-1.5 text-gray-500 shadow-md transition-colors hover:text-gray-800"
                    aria-label="Naar beneden scrollen"
                >
                    <ArrowDownIcon className="h-3.5 w-3.5" />
                </button>
            )}
        </div>
    );
}
